import { useState, useEffect } from 'react';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { generateActivityStoragePath } from '../../../utilities/storagePathHelper';
import { generateBatchSpeechAudio, LanguageCodes, VoiceGenders } from '../../../utilities/extensions/textToSpeech';
import { db } from '../../../firebase';

const NumbersActivity = ({ initialContent, onContentChange, chapterId, grade, selectedChapter }) => {
  const [content, setContent] = useState({
    fromNumber: 1,
    toNumber: 10,
    numbers: [
      {
        index: 1,
        englishNumber: '1',
        englishWordNumber: '',
        languageNumber: '',
        languageWordNumber: '',
        englishNumberAudio: null,
        languageNumberAudio: null,
        languageWordNumberAudio: null,
        confusingLetters: ''
      }
    ]
  });

  useEffect(() => {
    if (initialContent) {
      try {
        const parsed = JSON.parse(initialContent);
        setContent(parsed);
      } catch {
        // Keep default content if parsing fails
      }
    }
  }, [initialContent]);

  useEffect(() => {
    onContentChange(content);
  }, [content, onContentChange]);

  const [uploadingFiles, setUploadingFiles] = useState({});
  const [generatingAudio, setGeneratingAudio] = useState(false);

  const handleRangeChange = (field, value) => {
    const num = parseInt(value, 10);
    setContent(prev => ({
      ...prev,
      [field]: isNaN(num) ? '' : num
    }));
  };

  const buildStoragePath = ({ numberIndex, fileName }) => {
    const language = selectedChapter?.subject?.toLowerCase() || 'language';
    const gradeStr = `grade${grade}`;

    return generateActivityStoragePath({
      language,
      grade: gradeStr,
      chapterId,
      activityName: 'numbers',
      fileName,
      itemId: `number${numberIndex}`
    });
  };

  const handleFileUpload = async (index, fileType, file) => {
    if (!file || !chapterId || !grade) {
      return;
    }

    const uploadKey = `${index}-${fileType}`;
    setUploadingFiles(prev => ({ ...prev, [uploadKey]: true }));

    try {
      const storage = getStorage();

      const numberRow = content.numbers[index];
      const existingFilePath = numberRow[fileType];
      if (existingFilePath) {
        try {
          const existingFileRef = ref(storage, existingFilePath);
          await deleteObject(existingFileRef);
        } catch (deleteError) {
          // ignore delete error
        }
      }

      const storagePath = buildStoragePath({
        numberIndex: index + 1,
        fileName: file.name
      });

      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);

      const updated = [...(content.numbers || [])];
      updated[index] = {
        ...updated[index],
        [fileType]: storagePath
      };
      setContent(prev => ({ ...prev, numbers: updated }));
    } catch (error) {
      alert('Failed to upload file. Please try again.');
    } finally {
      setUploadingFiles(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const removeFile = async (index, fileType) => {
    const row = content.numbers[index];
    const filePath = row[fileType];

    if (filePath) {
      try {
        const storage = getStorage();
        const fileRef = ref(storage, filePath);
        await deleteObject(fileRef);
      } catch (error) {
        // ignore delete error
      }
    }

    const updated = [...(content.numbers || [])];
    updated[index] = { ...updated[index], [fileType]: null };
    setContent(prev => ({ ...prev, numbers: updated }));
  };

  const playAudio = async (audioPath) => {
    if (!audioPath) return;

    try {
      const storage = getStorage();
      const audioRef = ref(storage, audioPath);
      const downloadURL = await getDownloadURL(audioRef);
      const audio = new Audio(downloadURL);
      audio.play().catch(() => {});
    } catch (error) {
      alert(`Failed to play audio. Error: ${error.message}`);
    }
  };

  // Generate audio for existing numbers that are missing audio
  const generateAudioForExistingNumbers = async () => {
    if (!content.numbers || content.numbers.length === 0) {
      alert('No numbers available for audio generation');
      return;
    }

    const ttsRequests = [];

    content.numbers.forEach((row, index) => {
      // English number audio - prefer englishWordNumber ("one", "two", etc.),
      // and fall back to the numeric string if no word is provided.
      const englishText = (row.englishWordNumber && row.englishWordNumber.trim())
        ? row.englishWordNumber
        : row.englishNumber;

      if (englishText && englishText.trim() && !row.englishNumberAudio) {
        ttsRequests.push({
          text: englishText,
          languageCode: LanguageCodes.ENGLISH,
          ssmlGender: VoiceGenders.FEMALE,
          rowIndex: index,
          type: 'englishNumber'
        });
      }

      // Language numeral audio
      if (row.languageNumber && row.languageNumber.trim() && !row.languageNumberAudio) {
        ttsRequests.push({
          text: row.languageNumber,
          languageCode: LanguageCodes.KANNADA,
          ssmlGender: VoiceGenders.FEMALE,
          rowIndex: index,
          type: 'languageNumber'
        });
      }
    });

    if (ttsRequests.length === 0) {
      alert('All numbers already have audio for the available texts');
      return;
    }

    setGeneratingAudio(true);

    try {
      const storage = getStorage();

      const audioUrls = await generateBatchSpeechAudio(
        ttsRequests.map((req) => ({
          text: req.text,
          languageCode: req.languageCode,
          ssmlGender: req.ssmlGender
        })),
        db,
        storage
      );

      const updatedNumbers = [...(content.numbers || [])];

      ttsRequests.forEach((request, requestIndex) => {
        const audioUrl = audioUrls[requestIndex];
        if (!audioUrl || audioUrl.startsWith('[TTS Error:')) return;

        const { rowIndex, type } = request;
        if (type === 'englishNumber') {
          updatedNumbers[rowIndex].englishNumberAudio = audioUrl;
        } else if (type === 'languageNumber') {
          updatedNumbers[rowIndex].languageNumberAudio = audioUrl;
        }
      });

      setContent((prev) => ({ ...prev, numbers: updatedNumbers }));
    } catch (error) {
      alert('Failed to generate audio: ' + error.message);
    } finally {
      setGeneratingAudio(false);
    }
  };

  const toLanguageDigits = (num) => {
    const digitMap = {
      '0': '೦',
      '1': '೧',
      '2': '೨',
      '3': '೩',
      '4': '೪',
      '5': '೫',
      '6': '೬',
      '7': '೭',
      '8': '೮',
      '9': '೯'
    };

    return String(num)
      .split('')
      .map((ch) => digitMap[ch] ?? ch)
      .join('');
  };

  const generateNumbers = () => {
    const { fromNumber, toNumber } = content;
    const start = parseInt(fromNumber, 10);
    const end = parseInt(toNumber, 10);

    if (isNaN(start) || isNaN(end)) {
      alert('Please enter valid numbers for the range');
      return;
    }

    if (start > end) {
      alert('From number should be less than or equal to To number');
      return;
    }

    const generated = [];
    for (let i = start; i <= end; i += 1) {
      const english = String(i);
      const languageDigits = toLanguageDigits(i);
      generated.push({
        index: i,
        englishNumber: english,
        englishWordNumber: '',
        languageNumber: languageDigits,
        languageWordNumber: '',
        englishNumberAudio: null,
        languageNumberAudio: null,
        languageWordNumberAudio: null,
        confusingLetters: ''
      });
    }

    setContent(prev => ({
      ...prev,
      numbers: generated
    }));
  };

  const updateNumberRow = (rowIndex, field, value) => {
    const updated = [...(content.numbers || [])];
    updated[rowIndex] = { ...updated[rowIndex], [field]: value };
    setContent(prev => ({ ...prev, numbers: updated }));
  };

  const validate = () => {
    const errors = [];

    if (!content.numbers || content.numbers.length === 0) {
      errors.push('At least one number is required. Please set a range and click Generate.');
    }

    return errors;
  };

  const errors = validate();

  return (
    <div className="space-y-6">
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From Number</label>
              <input
                type="number"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                value={content.fromNumber}
                onChange={e => handleRangeChange('fromNumber', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">To Number</label>
              <input
                type="number"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                value={content.toNumber}
                onChange={e => handleRangeChange('toNumber', e.target.value)}
              />
            </div>
            <div className="flex md:justify-end">
              <button
                type="button"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-sm w-full md:w-auto"
                onClick={generateNumbers}
              >
                Generate
              </button>
            </div>
          </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">Numbers</h3>
            <p className="text-sm text-gray-600 mt-1">
              {content.numbers?.length || 0} number{(content.numbers?.length || 0) !== 1 ? 's' : ''} configured
            </p>
          </div>
          <button
            type="button"
            className="flex items-center justify-center px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium shadow-sm disabled:bg-orange-300 disabled:cursor-not-allowed text-sm"
            onClick={generateAudioForExistingNumbers}
            disabled={generatingAudio || !content.numbers || content.numbers.length === 0}
          >
            {generatingAudio ? (
              <>
                <span className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Generating Audio...
              </>
            ) : (
              <>
                <span className="mr-2">🎵</span>
                Generate Missing Audio
              </>
            )}
          </button>
        </div>
        <div className="p-6 space-y-4">
          {(content.numbers || []).map((row, idx) => (
            <div key={row.index || idx} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0">
                  {row.index}
                </div>
                <div className="flex-1">
                  <div className="grid grid-cols-1 md:grid-cols-7 gap-3 items-center">
                    <input
                      type="text"
                      className="px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-sm w-24 md:w-20"
                      value={row.englishNumber || ''}
                      readOnly
                    />
                    <input
                      type="text"
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm w-28 md:w-24"
                      placeholder="English word"
                      value={row.englishWordNumber || ''}
                      onChange={e => updateNumberRow(idx, 'englishWordNumber', e.target.value)}
                    />
                    <input
                      type="text"
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm w-24 md:w-20"
                      placeholder="Language number"
                      value={row.languageNumber || ''}
                      onChange={e => updateNumberRow(idx, 'languageNumber', e.target.value)}
                    />
                    <input
                      type="text"
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm md:col-span-2"
                      placeholder="Language word for number"
                      value={row.languageWordNumber || ''}
                      onChange={e => updateNumberRow(idx, 'languageWordNumber', e.target.value)}
                    />
                    <input
                      type="text"
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm md:col-span-2"
                      placeholder="Confusing letters (for letter game)"
                      value={row.confusingLetters || ''}
                      onChange={e => updateNumberRow(idx, 'confusingLetters', e.target.value)}
                    />
                    <div className="flex items-center gap-1 text-xs text-gray-600">
                      <input
                        type="file"
                        accept="audio/*"
                        className="hidden"
                        id={`en-audio-${idx}`}
                        onChange={(e) => handleFileUpload(idx, 'englishNumberAudio', e.target.files[0])}
                        disabled={uploadingFiles[`${idx}-englishNumberAudio`]}
                      />
                      <label
                        htmlFor={`en-audio-${idx}`}
                        className="cursor-pointer px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs hover:bg-blue-100 transition-colors"
                      >
                        EN 🎵
                      </label>
                      {uploadingFiles[`${idx}-englishNumberAudio`] && (
                        <span className="text-blue-600 text-[10px]">Uploading...</span>
                      )}
                      {row.englishNumberAudio && !uploadingFiles[`${idx}-englishNumberAudio`] && (
                        <>
                          <button
                            type="button"
                            className="p-1 text-green-600 hover:bg-green-100 rounded"
                            onClick={() => playAudio(row.englishNumberAudio)}
                            title="Play English audio"
                          >
                            ▶
                          </button>
                          <button
                            type="button"
                            className="p-1 text-red-600 hover:bg-red-100 rounded"
                            onClick={() => removeFile(idx, 'englishNumberAudio')}
                            title="Remove English audio"
                          >
                            ✕
                          </button>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-gray-600">
                      <input
                        type="file"
                        accept="audio/*"
                        className="hidden"
                        id={`lang-num-audio-${idx}`}
                        onChange={(e) => handleFileUpload(idx, 'languageNumberAudio', e.target.files[0])}
                        disabled={uploadingFiles[`${idx}-languageNumberAudio`]}
                      />
                      <label
                        htmlFor={`lang-num-audio-${idx}`}
                        className="cursor-pointer px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs hover:bg-blue-100 transition-colors"
                      >
                        LN # 🎵
                      </label>
                      {uploadingFiles[`${idx}-languageNumberAudio`] && (
                        <span className="text-blue-600 text-[10px]">Uploading...</span>
                      )}
                      {row.languageNumberAudio && !uploadingFiles[`${idx}-languageNumberAudio`] && (
                        <>
                          <button
                            type="button"
                            className="p-1 text-green-600 hover:bg-green-100 rounded"
                            onClick={() => playAudio(row.languageNumberAudio)}
                            title="Play language number audio"
                          >
                            ▶
                          </button>
                          <button
                            type="button"
                            className="p-1 text-red-600 hover:bg-red-100 rounded"
                            onClick={() => removeFile(idx, 'languageNumberAudio')}
                            title="Remove language number audio"
                          >
                            ✕
                          </button>
                        </>
                      )}
                    </div>
                    {/* Only two audio slots: English number and language number. No audio for language word. */}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {errors.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
          <div className="flex items-start">
            <div className="w-6 h-6 text-amber-600 mr-3 mt-0.5">
              <svg fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-amber-800 mb-2">Please fix the following issues:</h3>
              <ul className="space-y-1">
                {errors.map((error, index) => (
                  <li key={index} className="flex items-start text-amber-700">
                    <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-2 mr-2 flex-shrink-0" />
                    <span>{error}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const validateNumbersContent = (content) => {
  const errors = [];

  if (!content.numbers || content.numbers.length === 0) {
    errors.push('At least one number is required.');
  }

  return errors;
};

export default NumbersActivity;
